import React, { useCallback, useLayoutEffect, useRef } from 'react';
import cx from 'classnames';
import BucketTile from './BucketTile';

const POOL_MAX_SINGLE_ROW = 5;
const TILE_SIZE_VAR = '--bucketing-pool-tile-size';

// Split from the full pool, not from what is left in it, so the band keeps its height as
// tiles leave.
export function splitIntoRows(options) {
  if (options.length <= POOL_MAX_SINGLE_ROW) return [options];
  const topRowCount = Math.ceil(options.length / 2);
  return [options.slice(0, topRowCount), options.slice(topRowCount)];
}

export function largestSquareInRow(rowWidth, rowHeight, tileCount, gap) {
  if (!tileCount) return 0;
  const widthShare = (rowWidth - (tileCount - 1) * gap) / tileCount;
  return Math.max(0, Math.floor(Math.min(rowHeight, widthShare)));
}

// CSS cannot take the smaller of a row's height and a tile's share of its width, so the row is
// measured and publishes the side length its tiles should use.
function usePoolTileSize(bandRef) {
  const apply = useCallback(() => {
    const band = bandRef.current;
    if (!band) return;
    for (const row of band.children) {
      const tileCount = Number(row.dataset.rowCount) || 0;
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const side = largestSquareInRow(row.clientWidth, row.clientHeight, tileCount, gap);
      if (side > 0) row.style.setProperty(TILE_SIZE_VAR, `${side}px`);
    }
  }, [bandRef]);

  useLayoutEffect(() => {
    const band = bandRef.current;
    if (!band) return undefined;
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(band);
    return () => observer.disconnect();
  }, [bandRef, apply]);
}

export default function BucketingPool({
  pool,
  placedKeys,
  revealedKeys,
  selectedPoolKey,
  playingPoolKey,
  narrationPassActive,
  hidePool,
  pointerTarget,
  disabled,
  onPoolItemClick,
}) {
  const bandRef = useRef(null);
  usePoolTileSize(bandRef);

  if (pool.length === 0) return null;

  const rows = splitIntoRows(pool);
  const anyImageTiles = pool.some((option) => option.imageUrl);

  return (
    <div
      className={cx('bucketing__pool', {
        'bucketing__pool--image': anyImageTiles,
        hidden: hidePool,
      })}
    >
      <div
        ref={bandRef}
        className={cx('bucketing__poolTiles', `bucketing__poolTiles--rows${rows.length}`)}
      >
        {rows.map((row, rowIndex) => (
          <div className="bucketing__poolRow" key={`poolRow-${rowIndex}`} data-row-count={row.length}>
            {row
              .filter((option) => !placedKeys.has(option.key))
              .map((option) => (
                <BucketTile
                  key={option.key}
                  option={option}
                  selected={selectedPoolKey === option.key}
                  playing={playingPoolKey === option.key}
                  resting={narrationPassActive && playingPoolKey !== option.key}
                  revealed={revealedKeys.has(option.key)}
                  pointed={pointerTarget === option.key}
                  disabled={disabled || !revealedKeys.has(option.key)}
                  onClick={() => onPoolItemClick(option)}
                />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}
